import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {Offer} from '../../../../model/offer/offer';
import {VersionFieldChange} from '../../../../model/offer/offer-version-history-entry';
import {OfferType} from '../../../../enum/offer-type';

interface ParsedChange {
  dto: string;
  entityId: string | null;
  property: string;
}

/**
 * Read-only mirror of offers-details-tab used inside the Offer Version History dialog.
 * Renders a specific historical Offer version and decorates section headers with a
 * "Changed" badge when the corresponding fields appear in [changes].
 */
@Component({
  selector: 'app-offer-version-details-expandable',
  templateUrl: './offer-version-details-expandable.component.html',
  styleUrls: ['./offer-version-details-expandable.component.scss', '../../../../../styles/create-something.scss'],
  standalone: false
})
export class OfferVersionDetailsExpandableComponent implements OnChanges {

  @Input() offer: Offer;
  @Input() changes: VersionFieldChange[] = [];
  @Input() existingFileIds: Set<string> = new Set<string>();

  changedSections: Record<string, boolean> = {};

  readonly OfferType = OfferType;

  ngOnChanges(_changes: SimpleChanges): void {
    this.changedSections = this.computeChangedSections();
  }

  isChanged(section: string): boolean {
    return !!this.changedSections[section];
  }

  private parseField(field: string): ParsedChange | null {
    if (!field || typeof field !== 'string') return null;
    const match = /(?:\.\.\.|^|\.)([A-Z]\w+)\/([^.]*)\.(.+)$/.exec(field);
    if (!match) return null;
    const [, dto, entityIdRaw, property] = match;
    return {
      dto,
      entityId: entityIdRaw && entityIdRaw.length > 0 ? entityIdRaw : null,
      property
    };
  }

  private mapSection(parsed: ParsedChange): string | null {
    switch (parsed.dto) {
      case 'ExternalInsurerDto':
        return 'offerData';
      case 'BinaryDataDto':
        return 'files';
      case 'OfferDto':
        return OfferVersionDetailsExpandableComponent.offerSection(parsed.property);
      default:
        return null;
    }
  }

  private static offerSection(prop: string): string | null {
    if (['isLeading', 'amountCovered'].includes(prop)) {
      return 'management';
    }
    if (['premiumInPercent', 'premiumPerThousand', 'premium', 'expectedTotalPremium',
         'advancePremium', 'minimumPremium', 'typeOfDeductible', 'deductibleAbsoluteAmount',
         'deductibleCurrency', 'referenceValue', 'currency', 'offerDescription'].includes(prop)) {
      return 'premium';
    }
    if (prop === 'standardTermsAndConditions') return 'defaultTerms';
    if (prop === 'individualTermsAndConditions') return 'individualTerms';
    if (prop === 'standardClauses') return 'defaultClauses';
    if (prop === 'individualClauses') return 'individualClauses';
    if (prop === 'otherInformation') return 'otherInformation';
    if (prop === 'attachedFiles') return 'files';
    return null;
  }

  private computeChangedSections(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    if (!this.changes || this.changes.length === 0) return result;
    for (const change of this.changes) {
      const parsed = this.parseField(change?.field ?? '');
      if (!parsed) continue;
      const section = this.mapSection(parsed);
      if (section) result[section] = true;
    }
    return result;
  }
}
